'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth';
import { getOrCreateAnonymousId, getStoredAnonymousId } from '@/lib/fcm-utils';

export type SubscriptionEntityType = 'cruise' | 'region' | 'organization';

export interface Subscription {
  id: string;
  entity_type: string;
  entity_id: string;
  push_enabled?: boolean;
  email_enabled?: boolean;
}

function parseSubscriptionsResponse(data: unknown): Subscription[] {
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  const arr = o.subscriptions;
  if (Array.isArray(arr)) {
    return arr.filter(
      (s): s is Subscription =>
        s && typeof s === 'object' && typeof (s as Subscription).id === 'string' && typeof (s as Subscription).entity_type === 'string' && typeof (s as Subscription).entity_id === 'string'
    );
  }
  if (typeof o.id === 'string' && typeof o.entity_type === 'string' && typeof o.entity_id === 'string') {
    return [{ id: o.id, entity_type: o.entity_type, entity_id: o.entity_id, push_enabled: o.push_enabled as boolean | undefined, email_enabled: o.email_enabled as boolean | undefined }];
  }
  return [];
}

export function useSubscriptions() {
  const authStore = useAuthStore();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);

  const getAnonymousId = useCallback((): string | null => getStoredAnonymousId(), []);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      if (authStore.isLoggedIn) {
        const url = '/api/subscriptions';
        const res = await authStore.authorizedFetch(url);
        if (res.ok) {
          const data = await res.json();
          const list = parseSubscriptionsResponse(data);
          setSubscriptions(list);
        } else {
          setSubscriptions([]);
        }
      } else {
        const anonymousId = getAnonymousId();
        if (!anonymousId) {
          setSubscriptions([]);
          return;
        }
        const url = `/api/subscriptions?anonymous_id=${encodeURIComponent(anonymousId)}`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const list = parseSubscriptionsResponse(data);
          setSubscriptions(list);
        } else {
          setSubscriptions([]);
        }
      }
    } catch {
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [authStore, getAnonymousId]);

  useEffect(() => {
    if (authStore.authLoading) return;
    if (authStore.isLoggedIn) {
      // Clear so anonymous likes are not shown; fetch will set user's subscriptions
      setSubscriptions([]);
      fetchSubscriptions();
    } else {
      if (getAnonymousId()) fetchSubscriptions();
      else setSubscriptions([]);
    }
  }, [authStore.authLoading, authStore.isLoggedIn, fetchSubscriptions, getAnonymousId]);

  const isSubscribed = useCallback(
    (entityType: SubscriptionEntityType, entityId: string): boolean => {
      return subscriptions.some(
        (s) => s.entity_type === entityType && s.entity_id === entityId
      );
    },
    [subscriptions]
  );

  /** Merge server-provided is_liked from list responses so hearts show correctly on load */
  const mergeSubscriptionsFromServer = useCallback(
    (entityType: SubscriptionEntityType, items: Array<{ entity_id: string; is_liked?: boolean }>) => {
      const toAdd = items.filter((i) => i.is_liked === true);
      if (toAdd.length === 0) return;
      setSubscriptions((prev) => {
        const existing = new Set(prev.map((s) => `${s.entity_type}:${s.entity_id}`));
        const next = [...prev];
        for (const item of toAdd) {
          const key = `${entityType}:${item.entity_id}`;
          if (!existing.has(key)) {
            existing.add(key);
            next.push({ id: '', entity_type: entityType, entity_id: item.entity_id });
          }
        }
        return next;
      });
    },
    []
  );

  const toggleSubscribe = useCallback(
    async (entityType: SubscriptionEntityType, entityId: string): Promise<boolean> => {
      const subscribed = isSubscribed(entityType, entityId);

      if (authStore.isLoggedIn) {
        setSubscriptions((prev) => {
          if (subscribed) {
            return prev.filter(
              (s) => !(s.entity_type === entityType && s.entity_id === entityId)
            );
          }
          return [
            ...prev,
            {
              id: '',
              entity_type: entityType,
              entity_id: entityId,
              push_enabled: true,
              email_enabled: true,
            },
          ];
        });

        try {
          if (subscribed) {
            const res = await authStore.authorizedFetch('/api/subscriptions/by-entity', {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
            });
            if (!res.ok) {
              throw new Error('Unsubscribe failed');
            }
          } else {
            const res = await authStore.authorizedFetch('/api/subscriptions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                entity_type: entityType,
                entity_id: entityId,
                push_enabled: true,
                email_enabled: true,
              }),
            });
            if (!res.ok) {
              throw new Error('Subscribe failed');
            }
          }
          await fetchSubscriptions();
          return true;
        } catch {
          fetchSubscriptions();
          return false;
        }
      }

      // Anonymous: use FCM guest id when available, else client-generated id (subscribe without push until claim/login)
      const { id: anonymousId, pushEnabled } = await getOrCreateAnonymousId();

      if (!anonymousId) {
        return false;
      }

      setSubscriptions((prev) => {
        if (subscribed) {
          return prev.filter(
            (s) => !(s.entity_type === entityType && s.entity_id === entityId)
          );
        }
        return [
          ...prev,
          {
            id: '',
            entity_type: entityType,
            entity_id: entityId,
            push_enabled: true,
          },
        ];
      });

      try {
        if (subscribed) {
          const res = await fetch('/api/subscriptions/by-entity', {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entity_type: entityType,
              entity_id: entityId,
              anonymous_id: anonymousId,
            }),
          });
          if (!res.ok) throw new Error('Unsubscribe failed');
        } else {
          const res = await fetch('/api/subscriptions', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entity_type: entityType,
              entity_id: entityId,
              anonymous_id: anonymousId,
              push_enabled: pushEnabled,
            }),
          });
          if (!res.ok) throw new Error('Subscribe failed');
        }
        await fetchSubscriptions();
        return true;
      } catch {
        fetchSubscriptions();
        return false;
      }
    },
    [authStore, isSubscribed, fetchSubscriptions]
  );

  return {
    subscriptions,
    isSubscribed,
    toggleSubscribe,
    mergeSubscriptionsFromServer,
    loading,
    fetchSubscriptions,
  };
}
